// «Desarrollo Urbano · Territorio» — app del SO Orion para gobierno municipal.
// Visión por computadora + razonamiento sobre imagen satelital pública:
//   · Vegetación y obra: pérdida de vegetación 2019→2026 (Sentinel-2) con explicación.
//   · Arbolado: % de dosel por colonia (Meta/WRI 1 m) + demo de conteo árbol por árbol.
//   · Método y fuentes: límites honestos y qué falta del municipio.
// Demostración con capas precalculadas (public/gov-territory). Gate: backendModuleKey
// 'gov-territory' (fail-closed) — solo la org que lo tenga encendido lo ve.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Building2, Map as MapIcon, Trees } from 'lucide-react';

import { OsApp, OsTabs } from '@/modules/orion-shell/OsApp';
import { ErrorNote, Loading } from '@/modules/orion-shell/ui';
import { loadResumen, type Resumen } from './data';
import { HotspotsView } from './HotspotsView';
import { ArboladoView } from './ArboladoView';
import { MetodoView } from './MetodoView';
import './territory.css';

export type TerritoryTab = 'vegetacion' | 'arbolado' | 'metodo';

const TABS: { key: TerritoryTab; label: string; icon: typeof Building2 }[] = [
  { key: 'vegetacion', label: 'Vegetación', icon: Building2 },
  { key: 'arbolado', label: 'Arbolado', icon: Trees },
  { key: 'metodo', label: 'Método', icon: BookOpen },
];

export function TerritoryApp({ tab }: { tab: TerritoryTab }) {
  const navigate = useNavigate();
  const [resumen, setResumen] = useState<Resumen | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadResumen().then(setResumen).catch((e) => setError(e instanceof Error ? e.message : 'No se pudo cargar el análisis.'));
  }, []);

  return (
    <OsApp
      icon={MapIcon}
      title="Desarrollo Urbano · Territorio"
      subtitle={
        <span className="inline-flex items-center gap-1.5">
          <span className="rounded-full bg-primary/10 px-1.5 py-px text-[10.5px] font-semibold text-primary">Demostración</span>
          Datos: Sentinel-2 (ESA) · Meta/WRI · OSM · sin validación de campo
        </span>
      }
      tabs={<OsTabs label="Secciones de Territorio" value={tab} onChange={(t: TerritoryTab) => navigate(`/m/gov-territory/${t}`)} items={TABS} />}
    >
      {error && <ErrorNote>{error}</ErrorNote>}
      {!resumen && !error && <Loading label="Cargando análisis territorial…" />}
      {resumen && tab === 'vegetacion' && <HotspotsView resumen={resumen} />}
      {resumen && tab === 'arbolado' && <ArboladoView resumen={resumen} />}
      {resumen && tab === 'metodo' && <MetodoView resumen={resumen} />}
      {resumen && (
        <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
          Contiene datos modificados de Copernicus Sentinel 2019–2026 · Meta/WRI Canopy Height (CC-BY 4.0) · ESA WorldCover 2021 (CC-BY 4.0) ·
          © colaboradores de OpenStreetMap · OpenAerialMap (CC-BY 4.0). Datos derivados de OpenStreetMap (colonias y su % de dosel),
          disponibles bajo ODbL. Análisis generado el {resumen.generado}.
        </p>
      )}
    </OsApp>
  );
}
